import React, { useEffect, FC } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import type { CurrentRefinementsConnectorParamsRefinement } from 'instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements';
import type { Range } from 'node_modules/instantsearch.js/es/connectors/range/connectRange';
import {
  useClearRefinements,
  useCurrentRefinements,
  usePagination,
  useRange,
} from 'react-instantsearch';
import { css } from '@emotion/core';

import { Icon, colors, durations } from '../../../design-system';
import { HIERARCHICAL_ITEMS } from '../../catalog/components/HierarchicalMenu';
import { updateRefinementsItems } from '../actions';
import { getRefinementLabel } from '../utils';
import { theme } from '../../../design-system/systemprovider';

export const formatPriceStore = (
  refinement: CurrentRefinementsConnectorParamsRefinement[],
  range: Range
) => {
  if (refinement.length === 1) {
    return refinement[0].operator === '>='
      ? `${refinement[0].value}€ - ${range.max}€`
      : `${range.min}€ - ${refinement[0].value}€`;
  } else if (refinement.length === 2) {
    return `${refinement[0].value}€ - ${refinement[1].value}€`;
  }
  return null;
};

export const Refinements: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { items: currentRefinements, refine } = useCurrentRefinements({
    excludedAttributes: [...HIERARCHICAL_ITEMS, 'query'],
  });
  const { range } = useRange({ attribute: 'storePrice' });
  const { currentRefinement: pageRefinement } = usePagination();
  const { refine: clearRefine } = useClearRefinements({
    excludedAttributes: [...HIERARCHICAL_ITEMS, 'query'],
  });

  useEffect(() => {
    if (currentRefinements[0]) {
      dispatch(updateRefinementsItems(currentRefinements[0]?.refinements));
    }
  }, [currentRefinements]);

  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    const pageParam = searchParams.get('page');
    if (pageParam === '1') {
      searchParams.delete('page');
      navigate(`?${searchParams.toString()}`, { replace: true });
    }
  }, [pageRefinement, navigate]);

  return (
    <div
      css={css`
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px;
      `}
    >
      {currentRefinements.map((currentRefinement, index) => {
        if (currentRefinement.attribute === 'storePrice') {
          return (
            <button
              key={`currentRefinement_${index}`}
              data-testid={`remove-refinement-${currentRefinement.attribute}`}
              aria-label="Bouton pour supprimer le filtre de prix"
              type="button"
              onClick={() =>
                currentRefinement.refinements.forEach((refinement) => refine(refinement))
              }
              css={css`
                display: flex;
                justify-content: space-between;
                align-items:center;
                gap: 8px;
                height: 44px;
                min-width: 44px;
                border: 1px solid ${colors.BLACK};
                background: unset;
                border-radius: 4px;
                padding: 0 4px;
                color: ${colors.BLACK};
                cursor: pointer;
                transition: all ${durations.FOCUS_DELAY}ms ease-in-out;

                @media (hover: hover) {
                  &:hover {
                    background: ${colors.BACKGROUND};
                }
                `}
            >
              <p
                css={css`
                  max-width: ${theme.sizes.xh};
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                  margin: 0;
                  font-size: ${theme.fontSizes.s};
                `}
              >
                {formatPriceStore(currentRefinement.refinements, range)}
              </p>
              <Icon name="close" size={14} />
            </button>
          );
        }

        return currentRefinement.refinements.map((refinement) => (
          <button
            key={`currentRefinement_${refinement.value}_${index}`}
            data-testid={`remove-refinement-${refinement.value}`}
            aria-label={`Bouton pour supprimer le filtre ${getRefinementLabel(refinement.label)}`}
            type="button"
            onClick={() => refine(refinement)}
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: center;
              gap: 8px;
              height: 44px;
              min-width: 44px;
              border: 1px solid ${colors.BLACK};
              padding: 0 4px;
              color: ${colors.BLACK};
              background: unset;
              border-radius: 4px;
              cursor: pointer;
              transition: all ${durations.FOCUS_DELAY}ms ease-in-out;

              @media (hover: hover) {
                &:hover {
                  background: ${colors.BACKGROUND};
                }
              }
            `}
          >
            <p
              css={css`
                max-width: ${theme.sizes.xh};
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                margin: 0;
                font-size: ${theme.fontSizes.s};
              `}
            >
              {getRefinementLabel(refinement.label)}
            </p>
            <Icon name="close" size={14} />
          </button>
        ));
      })}
      {currentRefinements.length > 0 && (
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            height: 44px;
          `}
        >
          <button
            className="anchor-animated"
            type="button"
            data-testid="btn-reset-filters"
            aria-label="Bouton pour réinitialiser les filtres"
            id="btn-reset-filters"
            onClick={clearRefine}
            css={css`
              font-size: 1.4rem;
              position: relative;
              &::before {
                content: '';
                position: absolute;
                top: -15px;
                bottom: -15px;
                left: 0;
                right: 0;
                background: transparent;
              }
            `}
          >
            Réinitialiser les filtres
          </button>
        </div>
      )}
    </div>
  );
};
